import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  TextInput, Platform, Modal, useWindowDimensions, ActivityIndicator, Alert,
} from 'react-native';
import { useProfileStore, useUserStore } from '../../contexts/StoreContext';
import { ChildProfile, calculateAgeInMonths } from '../../types/profile';
import { WellCheckStorage, type GrowthDataDto, type WellCheckScheduleDto, type VaccineEventDto, type VaccineDto, type VaccineScheduleDto, type VaccineSchEvntDto } from '../../storage/WellCheckStorage';
import type { AdminResponseDto } from '../../types/AdminResponseDto';
import * as appConfig from '../../config/configManager';
import type { NavigationTab } from '../../types/navigation';
import { GrowthLineChart } from '../GrowthLineChart';
import { DevAgeLineChart, type DevAgeDataPoint } from '../DevAgeLineChart';
import { AddProfileView } from './AddProfileView';
import { DatePickerModal } from '../DatePickerModal';

// ─── Sub-tab definition ──────────────────────────────────────────────────────

type ProfileTab = 'Detail' | 'Growth' | 'DevAge' | 'Vaccinations';

const TABS: { key: ProfileTab; icon: string; label: string }[] = [
  { key: 'Detail',       icon: '👤', label: 'Details'      },
  { key: 'Growth',       icon: '📏', label: 'Growth'       },
  { key: 'DevAge',       icon: '📊', label: 'Development'  },
  { key: 'Vaccinations', icon: '💉', label: 'Vaccinations' },
];

const DOMAIN_LABELS: Record<string, string> = {
  communication:   'Communication',
  motor:           'Motor',
  finemotor:       'Fine Motor',
  grossmotor:      'Gross Motor',
  cognitive:       'Cognitive',
  socialemotional: 'Social-Emotional',
};

const GENDER_OPTIONS: ChildProfile['gender'][] = ['male', 'female', 'other', 'prefer-not-to-say'];
const GENDER_LABELS: Record<ChildProfile['gender'], string> = {
  'male': 'Male',
  'female': 'Female',
  'other': 'Other',
  'prefer-not-to-say': 'Prefer not to say',
};
const PREM_GEST_OPTIONS = Array.from({ length: 20 }, (_, i) => 40 - i); // 40..21

function ageFromDob(dob: string): string {
  if (!dob) return '—';
  const ageInMonths = calculateAgeInMonths(dob);
  if (!Number.isFinite(ageInMonths) || ageInMonths < 0) return '—';
  return `${ageInMonths.toFixed(1)} months old`;
}

function fmtDate(d: string): string {
  if (!d) return '—';
  // Parse YYYY-MM-DD as local midnight to avoid UTC off-by-one in local timezones
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  const dt = parts
    ? new Date(+parts[1], +parts[2] - 1, +parts[3])
    : new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function normalizeDateOnly(value?: string): string {
  if (!value) return '';
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(trimmed);
  if (isNaN(parsed.getTime())) return trimmed;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface ManageProfilesViewProps {
  onTabChange?: (tab: NavigationTab) => void;
  storage?: any;
}

export const ManageProfilesView: React.FC<ManageProfilesViewProps> = ({ onTabChange, storage }) => {
  const { width: screenWidth } = useWindowDimensions();
  const isWide = screenWidth >= 700;
  const isPremiumAccount = appConfig.isPremium();

  const profileStore = useProfileStore();
  const { profiles: profilesRaw, activeProfileId, setActiveProfile, addProfile, updateProfile, removeProfile, markProfileDirty, retryDirtyProfiles, syncProfileToBackend } =
    profileStore();
  const profiles = profilesRaw as ChildProfile[];

  const userStore = useUserStore();
  const accountEmail = userStore?.()?.session?.user?.email ?? '';

  // Selected profile for detail/growth/domain viewing (default = active profile)
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(activeProfileId);
  const [activeTab, setActiveTab] = useState<ProfileTab>('Detail');

  const [isSyncing, setIsSyncing] = useState(false);

  // Detail-tab edit state
  const [editName, setEditName] = useState('');       // display name (dname)
  const [editFname, setEditFname] = useState('');     // first name
  const [editLname, setEditLname] = useState('');     // last name
  const [editEmail, setEditEmail] = useState('');
  const [editDob, setEditDob] = useState('');
  const [editGender, setEditGender] = useState<ChildProfile['gender']>('prefer-not-to-say');
  const [editPremature, setEditPremature] = useState<0 | 1>(0);
  const [editPremGest, setEditPremGest] = useState(40);
  const premGestScrollRef = useRef<ScrollView>(null);
  const premGestScrollXRef = useRef(0);
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [showDobPicker, setShowDobPicker] = useState(false);

  // Confirm-disable modal
  const [disableTargetId, setDisableTargetId] = useState<string | null>(null);

  // Confirm-delete modal
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Show/hide disabled profiles in the chip selector
  const [showDisabled, setShowDisabled] = useState(false);

  // Profile hamburger menu
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const hamburgerBtnRef = useRef<any>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ top: number; left: number } | null>(null);

  // Add-profile overlay
  const [showAddProfile, setShowAddProfile] = useState(false);

  // Vaccinations tab inline-edit state
  // vacEditPeriod: which period card is currently open for editing (null = none)
  // vacEditDates:  working copy of eventId → ISO date string ('' = unchecked)
  const [vacEditPeriod, setVacEditPeriod] = useState<string | null>(null);
  const [vacEditDates, setVacEditDates] = useState<Map<string, string>>(new Map());
  const [vacDatePickerEventId, setVacDatePickerEventId] = useState<string | null>(null);

  // Vaccinations tab visibility — reads the same key as SettingsView
  const [vaccineInfoEnabled, setVaccineInfoEnabled] = useState(false);
  const [selectedVaccineSchedule, setSelectedVaccineSchedule] = useState<VaccineScheduleDto | null>(null);
  const [vaccineMap, setVaccineMap] = useState<Map<string, VaccineDto>>(new Map());

  // Growth tab
  const [trackHeightWeight, setTrackHeightWeight] = useState(false);
  const [measurementUnits, setMeasurementUnits] = useState<'english' | 'metric'>('english');
  const [wellCheckEventMap, setWellCheckEventMap] = useState<Map<string, string>>(new Map()); // eventId → visit name
  const [growthEditKey, setGrowthEditKey] = useState<string | null>(null); // rowKey of entry being edited
  const [growthEditDate, setGrowthEditDate] = useState('');
  const [growthEditHeight, setGrowthEditHeight] = useState('');
  const [growthEditWeight, setGrowthEditWeight] = useState('');

  // Development tab — completed Well-Check Snapshot responses for the selected profile
  const [snapshotResponses, setSnapshotResponses] = useState<AdminResponseDto[]>([]);

  const refreshWellCheckPreferences = useCallback(async () => {
    const activeStorage = storage || (globalThis as any).__childNavStorage;
    if (!activeStorage) {
      console.log('ManageProfilesView: Vaccinations refresh skipped - no storage available');
      return;
    }

    try {
      const vaccineEnabled = await activeStorage.getItem('wellcheck_vaccine_info_enabled');
      if (vaccineEnabled !== null) {
        setVaccineInfoEnabled(vaccineEnabled === 'true');
      }

      const units = await activeStorage.getItem('wellcheck_measurement_units');
      if (units) {
        setMeasurementUnits(units as 'english' | 'metric');
      }

      const trackHW = await activeStorage.getItem('wellcheck_track_height_weight');
      if (trackHW !== null) {
        setTrackHeightWeight(trackHW === 'true');
      }

      const wcs = new WellCheckStorage(activeStorage);
      const [lists, savedVacId, savedSchId] = await Promise.all([
        wcs.getAllWellCheckDefs(),
        activeStorage.getItem('wellcheck_selected_vaccine_schedule_id'),
        activeStorage.getItem('wellcheck_selected_schedule_id'),
      ] as const);

      const allVaccines: VaccineDto[] = lists.flatMap((l: any) => l.vaccines ?? []);
      setVaccineMap(new Map(allVaccines.map(v => [v.vaccineId, v])));

      const allVacSchedules: VaccineScheduleDto[] = lists.flatMap((l: any) => l.vacSchedules ?? l.vaccineSchedules ?? []);
      const selected = allVacSchedules.find(s => s.scheduleId === savedVacId) ?? allVacSchedules[0] ?? null;
      console.log('ManageProfilesView: Vaccinations refresh result', {
        storageType: activeStorage?.constructor?.name ?? typeof activeStorage,
        vaccineEnabled,
        savedVacId,
        savedSchId,
        wellCheckDefCount: lists.length,
        vacScheduleCount: allVacSchedules.length,
        vacScheduleIds: allVacSchedules.map(s => s.scheduleId),
        vacScheduleNames: allVacSchedules.map(s => s.name),
        selectedVacScheduleId: selected?.scheduleId ?? null,
        selectedVacScheduleName: selected?.name ?? null,
      });
      setSelectedVaccineSchedule(selected);

      const allScheds: WellCheckScheduleDto[] = lists.flatMap((l: any) => l.schedules ?? []);
      const activeSched = allScheds.find(s => s.scheduleId === savedSchId) ?? allScheds[0] ?? null;
      if (activeSched) {
        setWellCheckEventMap(new Map(activeSched.events.map(e => [e.eventId, e.name])));
      }
    } catch (error) {
      console.warn('ManageProfilesView: Vaccinations refresh failed', error);
    }
  }, [storage]);

  useEffect(() => {
    void refreshWellCheckPreferences();
  }, [refreshWellCheckPreferences]);

  useEffect(() => {
    if (activeTab === 'Vaccinations') {
      void refreshWellCheckPreferences();
    }
  }, [activeTab, refreshWellCheckPreferences]);

  // Load Well-Check Snapshot responses for the selected profile whenever it changes
  // or the Development tab becomes active.
  useEffect(() => {
    const activeStorage = storage || (globalThis as any).__childNavStorage;
    if (!activeStorage || !selectedProfileId) { setSnapshotResponses([]); return; }
    import('../../storage/AdminResponseStorage').then(({ AdminResponseStorage }) => {
      const ars = new AdminResponseStorage(activeStorage);
      return ars.getProfileResponses(selectedProfileId);
    }).then(profileResponses => {
      // Keep only Well-Check Snapshot responses (sourceId '4')
      const snapshots = (profileResponses.responses ?? []).filter(r =>
        r.recSurvey === '4' || r.recAssessment === '4'
      );
      // Sort oldest → newest
      snapshots.sort((a, b) =>
        new Date(a.recUTM || a.recDate || 0).getTime() -
        new Date(b.recUTM || b.recDate || 0).getTime()
      );
      setSnapshotResponses(snapshots);
    }).catch(() => { setSnapshotResponses([]); });
  }, [selectedProfileId, activeTab, storage]);

  const visibleProfiles = showDisabled ? profiles : profiles.filter(p => !p.isHidden);
  const hasHiddenProfiles = profiles.some(p => p.isHidden);
  const enabledProfiles = profiles.filter(p => !p.isHidden);
  const maxProfiles = isPremiumAccount ? 10 : 2;
  const canAddProfile = profiles.length < maxProfiles;
  const visibleTabs = TABS.filter(t =>
    (t.key !== 'Vaccinations' || vaccineInfoEnabled) &&
    (t.key !== 'Growth'       || trackHeightWeight) &&
    (t.key !== 'DevAge'       || isPremiumAccount)
  );

  useEffect(() => {
    if (!visibleTabs.some(t => t.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key ?? 'Detail');
    }
  }, [activeTab, visibleTabs]);

  // When the selected profile becomes hidden (e.g. just disabled) and we are not
  // in show-all mode, reactively switch selectedProfileId to another enabled profile.
  useEffect(() => {
    if (showDisabled) return;
    const sp = profiles.find(p => p.id === selectedProfileId);
    if (sp?.isHidden) {
      const other = profiles.find(p => !p.isHidden && p.id !== selectedProfileId);
      setSelectedProfileId(other?.id ?? null);
    }
  }, [profiles, selectedProfileId, showDisabled]);

  // selectedProfile resolves within visibleProfiles so a hidden profile is never
  // displayed when the "Show Disabled" filter is off.
  const selectedProfile =
    visibleProfiles.find(p => p.id === selectedProfileId) ??
    visibleProfiles[0] ??
    profiles[0] ??
    null;
  const genderIcon = selectedProfile?.gender === 'male' ? '👦' : selectedProfile?.gender === 'female' ? '👧' : '🧒';

  const handleProfileCreated = useCallback((newProfile: ChildProfile) => {
    if (!canAddProfile) {
      Alert.alert(
        'Profile Limit Reached',
        isPremiumAccount
          ? `Premium accounts can have up to ${maxProfiles} profiles.`
          : `Standard accounts can have up to ${maxProfiles} profiles. Upgrade to Premium for up to 10 profiles.`
      );
      return;
    }
    addProfile(newProfile);
    setShowAddProfile(false);
    setSelectedProfileId(newProfile.id ?? null);
    onTabChange?.('Milestones');
  }, [addProfile, canAddProfile, isPremiumAccount, maxProfiles, onTabChange]);

  // ── Detail tab handlers ───────────────────────────────────────────────────

  const handleSyncProfile = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      await retryDirtyProfiles();
    } catch (error) {
      Alert.alert('Sync Failed', 'Could not sync profile to server. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const beginEdit = useCallback(() => {
    if (!selectedProfile) return;
    setEditName(selectedProfile.name);
    setEditFname(selectedProfile.fname ?? '');
    setEditLname(selectedProfile.lname ?? '');
    setEditEmail(selectedProfile.email || accountEmail);
    setEditDob(normalizeDateOnly(selectedProfile.dob));
    setEditGender(selectedProfile.gender ?? 'prefer-not-to-say');
    setEditPremature(selectedProfile.cdc_premature === 1 ? 1 : 0);
    setEditPremGest(typeof selectedProfile.cdc_premgest === 'number' ? selectedProfile.cdc_premgest : 40);
    setShowDobPicker(false);
    setIsEditingDetail(true);
  }, [selectedProfile, accountEmail]);

  const saveDetail = useCallback(async () => {
    if (!selectedProfile) return;
    const name = editName.trim();
    if (!name) return;

    const dobValue = normalizeDateOnly(editDob) || normalizeDateOnly(selectedProfile.dob) || selectedProfile.dob;
    const editAgeMonths = calculateAgeInMonths(dobValue);
    const showPrematurityFields = Number.isFinite(editAgeMonths) && editAgeMonths < 24;
    const nextPremature: 0 | 1 = showPrematurityFields && editPremature === 1 ? 1 : 0;
    const nextPremGest = nextPremature === 1 ? Math.min(40, Math.max(21, Math.round(editPremGest))) : null;
    const nextPremWeek = nextPremGest !== null ? Math.max(0, 40 - nextPremGest) : null;

    const profileId = selectedProfile.id;
    updateProfile(selectedProfile.id, {
      name,
      fname: editFname.trim() || undefined,
      lname: editLname.trim() || undefined,
      email: editEmail.trim() || undefined,
      dob: dobValue,
      gender: editGender,
      cdc_premature: nextPremature,
      cdc_premgest: nextPremGest,
      cdc_premweek: nextPremWeek,
    });
    setIsEditingDetail(false);

    setIsSyncing(true);
    try {
      const ok = await syncProfileToBackend(profileId);
      if (!ok) {
        Alert.alert('Saved Locally', 'Profile details were saved locally but could not be synced right now.');
      }
    } catch (_error) {
      Alert.alert('Saved Locally', 'Profile details were saved locally but could not be synced right now.');
    } finally {
      setIsSyncing(false);
    }
  }, [selectedProfile, editName, editFname, editLname, editEmail, editDob, editGender, editPremature, editPremGest, updateProfile, syncProfileToBackend]);

  const scrollPremGestBy = useCallback((deltaX: number) => {
    const next = Math.max(0, premGestScrollXRef.current + deltaX);
    premGestScrollRef.current?.scrollTo({ x: next, animated: true });
    premGestScrollXRef.current = next;
  }, []);

  const syncProfileWithFallback = useCallback(async (profileId: string, failureMessage: string) => {
    setIsSyncing(true);
    try {
      const ok = await syncProfileToBackend(profileId);
      if (!ok) {
        Alert.alert('Saved Locally', failureMessage);
      }
    } catch (_error) {
      Alert.alert('Saved Locally', failureMessage);
    } finally {
      setIsSyncing(false);
    }
  }, [syncProfileToBackend]);

  // ── Manage tab handlers ───────────────────────────────────────────────────

  const confirmDisable = useCallback(async () => {
    if (!disableTargetId) return;
    const profileId = disableTargetId;
    // Switch active profile away to an enabled one if needed
    if (activeProfileId === profileId) {
      const other = enabledProfiles.find(p => p.id !== profileId);
      if (other) setActiveProfile(other.id);
    }
    // selectedProfileId is handled reactively by the useEffect above
    updateProfile(profileId, { isHidden: true, profileStatus: 0 });
    markProfileDirty(profileId, 'Profile disabled');
    setShowDisabled(false); // always return to enabled-only view after disabling
    setDisableTargetId(null);
    await syncProfileWithFallback(profileId, 'Profile status was saved locally but could not be synced right now.');
  }, [disableTargetId, activeProfileId, enabledProfiles, setActiveProfile, updateProfile, markProfileDirty, syncProfileWithFallback]);

  const handleEnableProfile = useCallback(async (profileId: string) => {
    updateProfile(profileId, { isHidden: false, profileStatus: 1 });
    markProfileDirty(profileId, 'Profile enabled');
    await syncProfileWithFallback(profileId, 'Profile status was saved locally but could not be synced right now.');
  }, [updateProfile, markProfileDirty, syncProfileWithFallback]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTargetId) return;
    const profileId = deleteTargetId;
    // Switch away from this profile if it is the active one
    if (activeProfileId === profileId) {
      const other = profiles.find(p => p.id !== profileId);
      if (other) setActiveProfile(other.id);
    }
    // Mark as deleted, sync status to backend, then remove locally
    updateProfile(profileId, { profileStatus: -1, isHidden: true });
    markProfileDirty(profileId, 'Profile deleted');
    setDeleteTargetId(null);
    await syncProfileWithFallback(profileId, 'Profile deletion was saved locally but could not be synced right now.');
    // Remove locally
    removeProfile(profileId);
  }, [deleteTargetId, activeProfileId, profiles, setActiveProfile, removeProfile, updateProfile, markProfileDirty, syncProfileWithFallback]);

  // ── Render helpers ────────────────────────────────────────────────────────

  // ── Detail tab ────────────────────────────────────────────────────────────

  const renderDetail = () => {
    if (!selectedProfile) return <Text style={styles.emptyText}>No profile selected.</Text>;
    if (isEditingDetail) {
      const editAgeMonths = calculateAgeInMonths(normalizeDateOnly(editDob) || normalizeDateOnly(selectedProfile.dob) || selectedProfile.dob);
      const showPrematurityFields = Number.isFinite(editAgeMonths) && editAgeMonths < 24;
      const editPremWeeks = editPremature === 1 ? Math.max(0, 40 - editPremGest) : 0;
      return (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>✏️ Edit Profile</Text>

          <Text style={styles.fieldLabel}>Profile Name (Display Name)</Text>
          {Platform.OS === 'web' ? (
            // @ts-ignore
            <input
              value={editName}
              onChange={(e: any) => setEditName(e.target.value)}
              style={{ padding: '8px 10px', fontSize: 14, border: '1px solid #d0d7de', borderRadius: 6,
                color: '#2c3e50', backgroundColor: '#fafbfc', marginBottom: 12, width: '100%', boxSizing: 'border-box' }}
            />
          ) : (
            <TextInput style={styles.textInput} value={editName} onChangeText={setEditName}
              placeholder="Profile display name" placeholderTextColor="#aaa" />
          )}

          <View style={{ flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1 }}>
              <Text style={styles.fieldLabel}>First Name</Text>
              {Platform.OS === 'web' ? (
                // @ts-ignore
                <input
                  value={editFname}
                  onChange={(e: any) => setEditFname(e.target.value)}
                  style={{ padding: '8px 10px', fontSize: 14, border: '1px solid #d0d7de', borderRadius: 6,
                    color: '#2c3e50', backgroundColor: '#fafbfc', marginBottom: 12, width: '100%', boxSizing: 'border-box' }}
                />
              ) : (
                <TextInput style={styles.textInput} value={editFname} onChangeText={setEditFname}
                  placeholder="First name" placeholderTextColor="#aaa" />
              )}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.fieldLabel}>Last Name</Text>
              {Platform.OS === 'web' ? (
                // @ts-ignore
                <input
                  value={editLname}
                  onChange={(e: any) => setEditLname(e.target.value)}
                  style={{ padding: '8px 10px', fontSize: 14, border: '1px solid #d0d7de', borderRadius: 6,
                    color: '#2c3e50', backgroundColor: '#fafbfc', marginBottom: 12, width: '100%', boxSizing: 'border-box' }}
                />
              ) : (
                <TextInput style={styles.textInput} value={editLname} onChangeText={setEditLname}
                  placeholder="Last name" placeholderTextColor="#aaa" />
              )}
            </View>
          </View>

          <Text style={styles.fieldLabel}>Email</Text>
          {Platform.OS === 'web' ? (
            // @ts-ignore
            <input
              type="email"
              value={editEmail}
              onChange={(e: any) => setEditEmail(e.target.value)}
              placeholder="Matches account email by default"
              style={{ padding: '8px 10px', fontSize: 14, border: '1px solid #d0d7de', borderRadius: 6,
                color: '#2c3e50', backgroundColor: '#fafbfc', marginBottom: 4, width: '100%', boxSizing: 'border-box' }}
            />
          ) : (
            <TextInput style={styles.textInput} value={editEmail} onChangeText={setEditEmail}
              placeholder="Matches account email by default" placeholderTextColor="#aaa"
              keyboardType="email-address" autoCapitalize="none" />
          )}
          <Text style={[styles.cardNote, { marginBottom: 12 }]}>Email can be changed for profile identification purposes.</Text>

          <Text style={styles.fieldLabel}>Date of Birth</Text>
          {Platform.OS === 'web' ? (
            // @ts-ignore
            <input
              type="date"
                value={normalizeDateOnly(editDob)}
              onChange={(e: any) => setEditDob(e.target.value)}
              style={{
                padding: '8px 10px',
                fontSize: 14,
                border: '1px solid #d0d7de',
                borderRadius: 6,
                color: '#2c3e50',
                backgroundColor: '#fafbfc',
                marginBottom: 12,
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <TouchableOpacity
              style={[styles.mobileDateButton, { marginBottom: 12 }]}
              onPress={() => setShowDobPicker(true)}
            >
              <Text style={[styles.mobileDateButtonText, !editDob.trim() && styles.mobileDateButtonPlaceholder]}>
                {editDob.trim() ? fmtDate(editDob) : 'Select date of birth'}
              </Text>
            </TouchableOpacity>
          )}

          {showPrematurityFields && (
            <View style={{ marginBottom: 12 }}>
              <TouchableOpacity
                style={styles.editCheckboxRow}
                onPress={() => setEditPremature(current => current === 1 ? 0 : 1)}
              >
                <View style={[styles.editCheckboxBox, editPremature === 1 && styles.editCheckboxBoxChecked]}>
                  {editPremature === 1 && <Text style={styles.editCheckboxCheck}>✓</Text>}
                </View>
                <Text style={styles.fieldLabel}>Born Premature</Text>
              </TouchableOpacity>

              {editPremature === 1 && (
                <View>
                  <Text style={styles.fieldLabel}>Gestation age at birth</Text>
                  <View style={[styles.premScrollRow, Platform.OS !== 'web' && styles.premScrollRowMobile]}>
                    {Platform.OS === 'web' && (
                      <TouchableOpacity
                        style={styles.premScrollButton}
                        onPress={() => scrollPremGestBy(-140)}
                      >
                        <Text style={styles.premScrollButtonText}>◀</Text>
                      </TouchableOpacity>
                    )}
                    <ScrollView
                      ref={premGestScrollRef}
                      horizontal
                      showsHorizontalScrollIndicator={Platform.OS === 'web'}
                      style={styles.premScrollView}
                      onScroll={(event) => {
                        premGestScrollXRef.current = event.nativeEvent.contentOffset.x;
                      }}
                      scrollEventThrottle={16}
                    >
                      <View style={styles.premChipRow}>
                        {PREM_GEST_OPTIONS.map(week => (
                          <TouchableOpacity
                            key={week}
                            style={[styles.premChip, editPremGest === week && styles.premChipActive]}
                            onPress={() => setEditPremGest(week)}
                          >
                            <Text style={[styles.premChipText, editPremGest === week && styles.premChipTextActive]}>{week}w</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </ScrollView>
                    {Platform.OS === 'web' && (
                      <TouchableOpacity
                        style={styles.premScrollButton}
                        onPress={() => scrollPremGestBy(140)}
                      >
                        <Text style={styles.premScrollButtonText}>▶</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  <Text style={styles.cardNote}>Weeks premature: {editPremWeeks}</Text>
                </View>
              )}
            </View>
          )}

          <Text style={styles.fieldLabel}>Gender</Text>
          <View style={styles.genderRow}>
            {GENDER_OPTIONS.map(g => (
              <TouchableOpacity key={g} style={[styles.genderChip, editGender === g && styles.genderChipActive]}
                onPress={() => setEditGender(g)}>
                <Text style={[styles.genderChipText, editGender === g && styles.genderChipTextActive]}>
                  {GENDER_LABELS[g]}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
            <TouchableOpacity style={styles.btnSecondary} onPress={() => setIsEditingDetail(false)}>
              <Text style={styles.btnSecondaryText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.btnPrimary} onPress={saveDetail}>
              <Text style={styles.btnPrimaryText}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    const p = selectedProfile;
    const fullName = [p.fname, p.lname].filter(Boolean).join(' ') || null;
    const premWeeksValue = p.cdc_premature === 1 && typeof p.cdc_premweek === 'number' ? p.cdc_premweek : null;
    const rows: { label: string; value: string }[] = [
      { label: 'Display Name',   value: p.name },
      ...(fullName ? [{ label: 'Full Name', value: fullName }] : []),
      ...(p.email ? [{ label: 'Email', value: p.email }] : []),
      { label: 'Date of Birth',  value: fmtDate(p.dob) },
      { label: 'Age',            value: ageFromDob(p.dob) },
      ...(premWeeksValue !== null ? [{ label: 'Weeks Premature', value: `${premWeeksValue}` }] : []),
      { label: 'Gender',         value: GENDER_LABELS[(p.gender ?? 'prefer-not-to-say') as ChildProfile['gender']] ?? '—' },
      { label: 'Status',         value: p.profileStatus === -1 ? 'Deleted' : p.profileStatus === 0 ? 'Disabled' : 'Active' },
      { label: 'Last Synced',    value: p.lastSyncDate ? fmtDate(p.lastSyncDate) : 'Not yet synced' },
    ];

    return (
      <View style={styles.card}>
        <View style={styles.cardTitleRow}>
          <Text style={styles.cardTitle}>{genderIcon} Child Profile</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {p.isDirty && (
              <TouchableOpacity
                style={[styles.syncIconButton, isSyncing && styles.syncIconButtonDisabled]}
                onPress={handleSyncProfile}
                disabled={isSyncing}
              >
                {isSyncing
                  ? <ActivityIndicator size="small" color="#3498db" />
                  : <Text style={styles.syncIconText}>🔄</Text>}
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.editBtn} onPress={beginEdit}>
              <Text style={styles.editBtnText}>Edit</Text>
            </TouchableOpacity>
          </View>
        </View>
        {rows.map(r => (
          <View key={r.label} style={styles.infoRow}>
            <Text style={styles.infoLabel}>{r.label}</Text>
            <Text style={styles.infoValue}>{r.value}</Text>
          </View>
        ))}
        {/* Profile actions */}
        <View style={{ flexDirection: 'row', gap: 10, marginTop: 12 }}>
          {p.id !== activeProfileId && !p.isHidden && (
            <TouchableOpacity style={styles.btnSecondary}
              onPress={() => { setActiveProfile(p.id); setSelectedProfileId(p.id); }}>
              <Text style={styles.btnSecondaryText}>★ Set Active</Text>
            </TouchableOpacity>
          )}
          {p.isHidden ? (
            <>
              <TouchableOpacity
                style={styles.btnSecondary}
                onPress={() => { void handleEnableProfile(p.id); }}
              >
                <Text style={styles.btnSecondaryText}>✓ Enable</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.btnDestructive}
                onPress={() => setDeleteTargetId(p.id)}
              >
                <Text style={styles.btnDestructiveText}>🗑 Delete</Text>
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity
              style={[styles.btnDanger, enabledProfiles.length <= 1 && styles.btnDisabled]}
              disabled={enabledProfiles.length <= 1}
              onPress={() => setDisableTargetId(p.id)}
            >
              <Text style={[styles.btnDangerText, enabledProfiles.length <= 1 && styles.btnDisabledText]}>⊘ Disable</Text>
            </TouchableOpacity>
          )}
        </View>
        {!p.isHidden && enabledProfiles.length <= 1 && (
          <Text style={styles.cardNote}>Cannot disable the only active profile.</Text>
        )}
      </View>
    );
  };

  // ── Growth tab ────────────────────────────────────────────────────────────

  const renderGrowth = () => {
    if (!selectedProfile) return <Text style={styles.emptyText}>No profile selected.</Text>;
    const data: GrowthDataDto[] = (selectedProfile.growthtrack?.data ?? []) as GrowthDataDto[];
    const sorted = [...data].filter(d => d.date).sort((a, b) => a.date.localeCompare(b.date));
    const heightLabel = measurementUnits === 'metric' ? 'cm' : 'in';
    const weightLabel = measurementUnits === 'metric' ? 'kg' : 'lb';

    if (sorted.length === 0) {
      return (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📏 Height & Weight Measurements</Text>
          <Text style={styles.emptyText}>No measurements recorded yet.</Text>
          {growthEditKey === '__new__' && (
            <View style={{ paddingHorizontal: 4, paddingVertical: 10, backgroundColor: '#f0f7ff', borderRadius: 6, marginTop: 8 }}>
              <View style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <View>
                  <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Date</Text>
                  {Platform.OS === 'web' ? (
                    // @ts-ignore
                    <input type="date" value={growthEditDate}
                      onChange={(e: any) => setGrowthEditDate(e.target.value)}
                      style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 136 }}
                    />
                  ) : (
                    <TextInput
                      style={[styles.textInput, { minWidth: 120, marginBottom: 0 }]}
                      value={growthEditDate} onChangeText={setGrowthEditDate}
                      placeholder="YYYY-MM-DD" placeholderTextColor="#aaa"
                    />
                  )}
                </View>
                <View>
                  <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Height ({heightLabel})</Text>
                  {Platform.OS === 'web' ? (
                    // @ts-ignore
                    <input type="number" value={growthEditHeight} step="0.5" min="0"
                      onChange={(e: any) => setGrowthEditHeight(e.target.value)}
                      placeholder="0"
                      style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 80, textAlign: 'right' }}
                    />
                  ) : (
                    <TextInput
                      style={[styles.textInput, { minWidth: 72, marginBottom: 0 }]}
                      value={growthEditHeight} onChangeText={setGrowthEditHeight}
                      placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad"
                    />
                  )}
                </View>
                <View>
                  <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Weight ({weightLabel})</Text>
                  {Platform.OS === 'web' ? (
                    // @ts-ignore
                    <input type="number" value={growthEditWeight} step="0.5" min="0"
                      onChange={(e: any) => setGrowthEditWeight(e.target.value)}
                      placeholder="0"
                      style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 80, textAlign: 'right' }}
                    />
                  ) : (
                    <TextInput
                      style={[styles.textInput, { minWidth: 72, marginBottom: 0 }]}
                      value={growthEditWeight} onChangeText={setGrowthEditWeight}
                      placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad"
                    />
                  )}
                </View>
              </View>
              <View style={{ flexDirection: 'row', gap: 16, marginTop: 10 }}>
                <TouchableOpacity
                  onPress={() => {
                    if (!selectedProfile) return;
                    const newDate = growthEditDate.trim();
                    const newH = growthEditHeight.trim();
                    const newW = growthEditWeight.trim();
                    if (!newDate) return;
                    const currentData: GrowthDataDto[] = (selectedProfile.growthtrack?.data ?? []) as GrowthDataDto[];
                    const updated = [...currentData, { date: newDate, height: newH, weight: newW }];
                    updateProfile(selectedProfile.id, { growthtrack: { data: updated } });
                    markProfileDirty(selectedProfile.id, 'Growth entry added');
                    setGrowthEditKey(null);
                  }}
                >
                  <Text style={styles.vacEditLink}>Save</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setGrowthEditKey(null)}>
                  <Text style={styles.vacEditLink}>Cancel</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
          {growthEditKey !== '__new__' && (
            <TouchableOpacity
              style={{ marginTop: 12, alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 14, backgroundColor: '#3498db', borderRadius: 6 }}
              onPress={() => {
                setGrowthEditDate(new Date().toISOString().split('T')[0]);
                setGrowthEditHeight('');
                setGrowthEditWeight('');
                setGrowthEditKey('__new__');
              }}
            >
              <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>+ Add Measurement</Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }

    return (
      <>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📈 Growth Chart</Text>
          <Text style={styles.cardSubtitle}>Height ({heightLabel}) and Weight ({weightLabel}) over time</Text>
          <GrowthLineChart data={sorted} heightLabel={heightLabel} weightLabel={weightLabel} dob={selectedProfile.dob} />
        </View>
        <View style={styles.card}>
        <Text style={styles.cardTitle}>📏 Height & Weight Measurements</Text>
        <Text style={styles.cardSubtitle}>{sorted.length} measurement{sorted.length !== 1 ? 's' : ''} recorded</Text>

        {/* Column headers */}
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Date</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>{''}</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>Height ({heightLabel})</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>Weight ({weightLabel})</Text>
          <Text style={[styles.tableHeaderCell, { width: 44 }]}>{''}</Text>
        </View>

        {growthEditKey === '__new__' && (
          <View style={{ paddingHorizontal: 4, paddingVertical: 10, backgroundColor: '#f0f7ff', borderRadius: 6, marginBottom: 8 }}>
            <View style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <View>
                <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Date</Text>
                {Platform.OS === 'web' ? (
                  // @ts-ignore
                  <input type="date" value={growthEditDate}
                    onChange={(e: any) => setGrowthEditDate(e.target.value)}
                    style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 136 }}
                  />
                ) : (
                  <TextInput
                    style={[styles.textInput, { minWidth: 120, marginBottom: 0 }]}
                    value={growthEditDate} onChangeText={setGrowthEditDate}
                    placeholder="YYYY-MM-DD" placeholderTextColor="#aaa"
                  />
                )}
              </View>
              <View>
                <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Height ({heightLabel})</Text>
                {Platform.OS === 'web' ? (
                  // @ts-ignore
                  <input type="number" value={growthEditHeight} step="0.5" min="0"
                    onChange={(e: any) => setGrowthEditHeight(e.target.value)}
                    placeholder="0"
                    style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 80, textAlign: 'right' }}
                  />
                ) : (
                  <TextInput
                    style={[styles.textInput, { minWidth: 72, marginBottom: 0 }]}
                    value={growthEditHeight} onChangeText={setGrowthEditHeight}
                    placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad"
                  />
                )}
              </View>
              <View>
                <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Weight ({weightLabel})</Text>
                {Platform.OS === 'web' ? (
                  // @ts-ignore
                  <input type="number" value={growthEditWeight} step="0.5" min="0"
                    onChange={(e: any) => setGrowthEditWeight(e.target.value)}
                    placeholder="0"
                    style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 80, textAlign: 'right' }}
                  />
                ) : (
                  <TextInput
                    style={[styles.textInput, { minWidth: 72, marginBottom: 0 }]}
                    value={growthEditWeight} onChangeText={setGrowthEditWeight}
                    placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad"
                  />
                )}
              </View>
            </View>
            <View style={{ flexDirection: 'row', gap: 16, marginTop: 10 }}>
              <TouchableOpacity
                onPress={() => {
                  if (!selectedProfile) return;
                  const newDate = growthEditDate.trim();
                  const newH = growthEditHeight.trim();
                  const newW = growthEditWeight.trim();
                  if (!newDate) return;
                  const currentData: GrowthDataDto[] = (selectedProfile.growthtrack?.data ?? []) as GrowthDataDto[];
                  const updated = [...currentData, { date: newDate, height: newH, weight: newW }];
                  updateProfile(selectedProfile.id, { growthtrack: { data: updated } });
                  markProfileDirty(selectedProfile.id, 'Growth entry added');
                  setGrowthEditKey(null);
                }}
              >
                <Text style={styles.vacEditLink}>Save</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setGrowthEditKey(null)}>
                <Text style={styles.vacEditLink}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {sorted.map((d, i) => {
          const rowKey = d.eventId ?? `${d.date}-${i}`;
          const isEditing = growthEditKey === rowKey;
          const eventName = d.eventId ? wellCheckEventMap.get(d.eventId) : undefined;
          return (
            <View key={rowKey}>
              {/* Data row */}
              <View style={[styles.tableRow, i % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]}>
                <Text style={[styles.tableCell, { flex: 2 }]}>{fmtDate(d.date)}</Text>
                <Text style={[styles.tableCell, { flex: 2, color: '#7f8c8d', fontSize: 12 }]}>
                  {eventName ?? ''}
                </Text>
                <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>
                  {d.height ? d.height : '—'}
                </Text>
                <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>
                  {d.weight ? `${d.weight}${d.weightOz ? ` lb ${d.weightOz} oz` : ''}` : '—'}
                </Text>
                <TouchableOpacity
                  style={{ width: 44, alignItems: 'flex-end', justifyContent: 'center' }}
                  onPress={() => {
                    if (isEditing) return; // Cancel is inside the edit area
                    setGrowthEditDate(d.date);
                    setGrowthEditHeight(d.height ?? '');
                    setGrowthEditWeight(d.weight ?? '');
                    setGrowthEditKey(rowKey);
                  }}
                >
                  {!isEditing && <Text style={styles.vacEditLink}>Edit</Text>}
                </TouchableOpacity>
              </View>

              {/* Inline edit form */}
              {isEditing && (
                <View style={{ paddingHorizontal: 4, paddingVertical: 10, backgroundColor: '#f0f7ff', borderRadius: 6, marginBottom: 4 }}>
                  <View style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    {/* Date */}
                    <View>
                      <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Date</Text>
                      {Platform.OS === 'web' ? (
                        // @ts-ignore
                        <input type="date" value={growthEditDate}
                          onChange={(e: any) => setGrowthEditDate(e.target.value)}
                          style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 136 }}
                        />
                      ) : (
                        <TextInput
                          style={[styles.textInput, { minWidth: 120, marginBottom: 0 }]}
                          value={growthEditDate} onChangeText={setGrowthEditDate}
                          placeholder="YYYY-MM-DD" placeholderTextColor="#aaa"
                        />
                      )}
                    </View>
                    {/* Height */}
                    <View>
                      <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Height ({heightLabel})</Text>
                      {Platform.OS === 'web' ? (
                        // @ts-ignore
                        <input type="number" value={growthEditHeight} step="0.5" min="0"
                          onChange={(e: any) => setGrowthEditHeight(e.target.value)}
                          placeholder="0"
                          style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 80, textAlign: 'right' }}
                        />
                      ) : (
                        <TextInput
                          style={[styles.textInput, { minWidth: 72, marginBottom: 0 }]}
                          value={growthEditHeight} onChangeText={setGrowthEditHeight}
                          placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad"
                        />
                      )}
                    </View>
                    {/* Weight */}
                    <View>
                      <Text style={{ fontSize: 11, color: '#7f8c8d', marginBottom: 3 }}>Weight ({weightLabel})</Text>
                      {Platform.OS === 'web' ? (
                        // @ts-ignore
                        <input type="number" value={growthEditWeight} step="0.5" min="0"
                          onChange={(e: any) => setGrowthEditWeight(e.target.value)}
                          placeholder="0"
                          style={{ border: '1px solid #d0d7de', borderRadius: 6, padding: '5px 8px', fontSize: 13, color: '#2c3e50', backgroundColor: '#fafbfc', outline: 'none', width: 80, textAlign: 'right' }}
                        />
                      ) : (
                        <TextInput
                          style={[styles.textInput, { minWidth: 72, marginBottom: 0 }]}
                          value={growthEditWeight} onChangeText={setGrowthEditWeight}
                          placeholder="0" placeholderTextColor="#aaa" keyboardType="decimal-pad"
                        />
                      )}
                    </View>
                  </View>
                  {/* Done / Cancel links */}
                  <View style={{ flexDirection: 'row', gap: 16, marginTop: 10 }}>
                    <TouchableOpacity
                      onPress={() => {
                        if (!selectedProfile) return;
                        const newDate = growthEditDate.trim();
                        const newH = growthEditHeight.trim();
                        const newW = growthEditWeight.trim();
                        const currentData: GrowthDataDto[] = (selectedProfile.growthtrack?.data ?? []) as GrowthDataDto[];
                        const updated = currentData.map(entry => {
                          const matches = d.eventId
                            ? entry.eventId === d.eventId
                            : entry.date === d.date && entry.height === d.height && entry.weight === d.weight;
                          if (!matches) return entry;
                          return { ...entry, date: newDate || entry.date, height: newH, weight: newW };
                        });
                        updateProfile(selectedProfile.id, { growthtrack: { data: updated } });
                        markProfileDirty(selectedProfile.id, 'Growth entry updated');
                        setGrowthEditKey(null);
                      }}
                    >
                      <Text style={styles.vacEditLink}>Done</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setGrowthEditKey(null)}>
                      <Text style={styles.vacEditLink}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          );
        })}

        {growthEditKey !== '__new__' && (
          <TouchableOpacity
            style={{ marginTop: 10, alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 14, backgroundColor: '#3498db', borderRadius: 6 }}
            onPress={() => {
              setGrowthEditDate(new Date().toISOString().split('T')[0]);
              setGrowthEditHeight('');
              setGrowthEditWeight('');
              setGrowthEditKey('__new__');
            }}
          >
            <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>+ Add Measurement</Text>
          </TouchableOpacity>
        )}
        </View>
      </>
    );
  };

  // ── Dev Age tab ───────────────────────────────────────────────────────────

  const renderDevAge = () => {
    if (!selectedProfile) return <Text style={styles.emptyText}>No profile selected.</Text>;

    if (snapshotResponses.length === 0) {
      return (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📊 Development</Text>
          <Text style={styles.emptyText}>No Well-Check Snapshot assessments recorded yet for this profile.</Text>
        </View>
      );
    }

    // Domain → CDC backend code map (must match WellVisitView prefill names)
    const CDC_DOMAIN_CODE: Record<string, string> = {
      communication:   'lc',
      motor:           'mt',
      finemotor:       'fm',
      grossmotor:      'gm',
      cognitive:       'cg',
      socialemotional: 'se',
    };

    // Build chart data — one point per snapshot, domains filtered same as snapshot cards
    const chartPoints: DevAgeDataPoint[] = snapshotResponses
      .map(resp => {
        const gv = (name: string): string | null =>
          resp.responseData?.find(d => d.name === name)?.value ?? null;
        const physicalAge = parseFloat(gv('agephysical') ?? '0') || 0;
        if (!physicalAge) return null;
        // Detect standard vs premium the same way the snapshot cards do
        const isPremium = !!(gv('cdc_fm_delay') || gv('cdc_gm_delay'));
        const activeDomains = isPremium
          ? ['communication', 'finemotor', 'grossmotor', 'cognitive', 'socialemotional']
          : ['communication', 'motor', 'cognitive', 'socialemotional'];
        const point: DevAgeDataPoint = { physicalAge };
        for (const domain of activeDomains) {
          const v = gv(`cdc_${CDC_DOMAIN_CODE[domain]}_score`);
          if (v !== null && v !== '') point[domain] = parseFloat(v) || null;
        }
        return point;
      })
      .filter((p): p is DevAgeDataPoint => p !== null);

    return (
      <View>
        {/* Summary chart card */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📊 Development Chart</Text>
          <Text style={styles.cardSubtitle}>Developmental age by domain over time</Text>
          <DevAgeLineChart data={chartPoints} />
        </View>

        {snapshotResponses.map((resp, idx) => {
          const getVal = (name: string): string | null =>
            resp.responseData?.find(d => d.name === name)?.value ?? null;

          const agephysical = getVal('agephysical');

          // Detect standard vs premium by presence of fine/gross motor delay data
          const isPremium = !!(getVal('cdc_fm_delay') || getVal('cdc_gm_delay'));
          const activeDomains = isPremium
            ? ['communication', 'finemotor', 'grossmotor', 'cognitive', 'socialemotional']
            : ['communication', 'motor', 'cognitive', 'socialemotional'];

          const rows = activeDomains
            .map(domain => ({
              domain,
              label: DOMAIN_LABELS[domain] ?? domain,
              age: getVal(`cdc_${CDC_DOMAIN_CODE[domain]}_score`),
              delay: getVal(`cdc_${CDC_DOMAIN_CODE[domain]}_delay`),
            }))
            .filter(r => r.age !== null && r.age !== '');

          // Card header details
          const cardDate = resp.recDate || resp.recUTM || '';
          const eventId = getVal('wellcheckeventid');
          const eventName = eventId ? (wellCheckEventMap.get(eventId) ?? null) : null;

          return (
            <View key={resp.recId ?? `snap-${idx}`} style={[styles.card, { marginBottom: 8 }]}>
              <Text style={styles.cardTitle}>📸 Snapshot</Text>
              {!!cardDate && (
                <Text style={[styles.cardSubtitle, { marginBottom: 0 }]}>{fmtDate(cardDate)}</Text>
              )}
              {!!eventName && (
                <Text style={[styles.cardSubtitle, { color: '#2980b9', marginBottom: 0 }]}>{eventName}</Text>
              )}
              {!!agephysical && (
                <Text style={[styles.cardSubtitle, { marginBottom: 6 }]}>Age at snapshot: {agephysical}mo</Text>
              )}

              {rows.length > 0 ? (
                <>
                  {/* Column headers */}
                  <View style={styles.tableHeader}>
                    <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Domain</Text>
                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>Dev Age</Text>
                    <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>Delay</Text>
                  </View>

                  {rows.map((r, i) => {
                    const delayNum = r.delay !== null && r.delay !== '' ? Number(r.delay) : null;
                    const delayColor = delayNum === null ? '#888' : delayNum === 0 ? '#27ae60' : delayNum <= 2 ? '#e67e22' : '#e74c3c';
                    return (
                      <View key={r.domain}
                        style={[styles.tableRow, i % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]}>
                        <Text style={[styles.tableCell, { flex: 2 }]}>{r.label}</Text>
                        <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>
                          {r.age !== null ? `${r.age}mo` : '—'}
                        </Text>
                        <Text style={[styles.tableCell, { flex: 1, textAlign: 'right', color: delayColor, fontWeight: '600' }]}>
                          {delayNum === null ? '—' : delayNum === 0 ? 'None' : `${delayNum}mo`}
                        </Text>
                      </View>
                    );
                  })}
                </>
              ) : (
                <Text style={styles.emptyText}>No domain data in this snapshot.</Text>
              )}
            </View>
          );
        })}
      </View>
    );
  };

  // ── Vaccinations tab ──────────────────────────────────────────────────────

  const renderVaccinations = () => {
    if (!selectedProfile) return <Text style={styles.emptyText}>No profile selected.</Text>;

    if (!selectedVaccineSchedule) {
      console.log('ManageProfilesView: No vaccination schedule available during render', {
        activeTab,
        vaccineInfoEnabled,
        selectedProfileId,
        storageProvided: !!storage,
        globalStoragePresent: !!((globalThis as any).__childNavStorage),
      });
      return (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>💉 Vaccination Record</Text>
          <View style={styles.vaccineNote}>
            <Text style={styles.vaccineNoteText}>
              💡 No vaccination schedule selected. Choose one under Settings → Preferences.
            </Text>
          </View>
        </View>
      );
    }

    // Build lookup: eventId → administered record (fallback: name)
    const administered = (selectedProfile.vaccinetrack?.events ?? []) as VaccineEventDto[];
    const byEventId = new Map<string, VaccineEventDto>();
    const byName = new Map<string, VaccineEventDto>();
    for (const e of administered) {
      if (e.eventId) byEventId.set(e.eventId, e);
      if (!byName.has(e.name)) byName.set(e.name, e); // keep first occurrence
    }

    // Group schedule events by period, preserving order
    const periodMap = new Map<string, VaccineSchEvntDto[]>();
    for (const evt of selectedVaccineSchedule.events) {
      const key = evt.period || '—';
      if (!periodMap.has(key)) periodMap.set(key, []);
      periodMap.get(key)!.push(evt);
    }

    const totalScheduled = selectedVaccineSchedule.events.length;
    const totalReceived = selectedVaccineSchedule.events.filter(
      evt => byEventId.has(evt.eventId) || byName.has(evt.name)
    ).length;

    const profileAgeInDays = (() => {
      const dob = selectedProfile?.dob;
      if (!dob) return 0;
      const birth = new Date(dob);
      return isNaN(birth.getTime()) ? 0 : Math.floor((Date.now() - birth.getTime()) / 86400000);
    })();

    return (
      <View>
        <View style={[styles.card, { paddingBottom: 8 }]}>
          <Text style={styles.cardTitle}>💉 Vaccination Record</Text>
          <Text style={styles.cardSubtitle}>Schedule: {selectedVaccineSchedule.name}</Text>
        </View>

        {Array.from(periodMap.entries()).map(([period, events], pi) => {
          const isEditing = vacEditPeriod === period;

          const openEdit = () => {
            // Seed working copy from current profile data
            const draft = new Map<string, string>();
            for (const evt of events) {
              const rec = byEventId.get(evt.eventId) ?? byName.get(evt.name);
              draft.set(evt.eventId, rec?.date ?? '');
            }
            setVacEditDates(draft);
            setVacEditPeriod(period);
          };

          const saveEdit = () => {
            if (!selectedProfile) return;
            // Rebuild the full vaccinetrack.events:
            // keep any events NOT belonging to this period, then add updated ones
            const periodEventIds = new Set(events.map(e => e.eventId));
            const periodNames    = new Set(events.map(e => e.name));
            const existing = (selectedProfile.vaccinetrack?.events ?? []) as VaccineEventDto[];
            const kept = existing.filter(
              e => !(e.eventId ? periodEventIds.has(e.eventId) : periodNames.has(e.name))
            );
            const added: VaccineEventDto[] = [];
            for (const evt of events) {
              const d = vacEditDates.get(evt.eventId) ?? '';
              if (d.trim()) {
                added.push({ name: evt.name, date: d.trim(), eventId: evt.eventId });
              }
            }
            updateProfile(selectedProfile.id, { vaccinetrack: { events: [...kept, ...added] } });
            markProfileDirty(selectedProfile.id, `VaccineTrack updated for period: ${period}`);
            setVacEditPeriod(null);
          };

          return (
            <View key={pi} style={[styles.card, styles.vacPeriodCard]}>
              {/* Period header row with Edit / Done link */}
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                <Text style={[styles.vacPeriodTitle, { flex: 1, marginBottom: 0 }]}>{period}</Text>
                <TouchableOpacity onPress={isEditing ? saveEdit : openEdit}>
                  <Text style={styles.vacEditLink}>{isEditing ? 'Done' : 'Edit'}</Text>
                </TouchableOpacity>
                {isEditing && (
                  <TouchableOpacity onPress={() => setVacEditPeriod(null)} style={{ marginLeft: 12 }}>
                    <Text style={[styles.vacEditLink, { color: '#aaa' }]}>Cancel</Text>
                  </TouchableOpacity>
                )}
              </View>

              {events.map((evt, ei) => {
                const rec = byEventId.get(evt.eventId) ?? byName.get(evt.name);
                const isMissing = !rec && (parseInt(evt.days) || 0) <= profileAgeInDays;
                if (!isEditing) {
                  return (
                    <View key={ei} style={styles.bulletRow}>
                      <Text style={[styles.bulletDot, rec ? { color: '#27ae60' } : isMissing ? { color: '#e74c3c' } : {}]}>{'•'}</Text>
                      <Text style={[styles.vacEventText, !rec ? (isMissing ? { color: '#e74c3c', fontWeight: '400' } : { color: '#7f8c8d', fontWeight: '400' }) : {}]}>{evt.name}
                        {rec
                          ? <Text style={[styles.vacEventDetail, { color: '#27ae60' }]}>{`  ✓ ${fmtDate(rec.date)}`}</Text>
                          : isMissing
                            ? <Text style={[styles.vacEventDetail, { color: '#e74c3c' }]}>{'  missing'}</Text>
                            : null}
                      </Text>
                    </View>
                  );
                }

                // ── Edit mode: checkbox + date input ──────────────────────
                const editDate = vacEditDates.get(evt.eventId) ?? '';
                const isChecked = editDate.trim() !== '';
                const today = new Date().toISOString().slice(0, 10);

                const toggleCheck = () => {
                  const next = new Map(vacEditDates);
                  next.set(evt.eventId, isChecked ? '' : today);
                  setVacEditDates(next);
                  if (isChecked && vacDatePickerEventId === evt.eventId) {
                    setVacDatePickerEventId(null);
                  }
                };
                const setDate = (d: string) => {
                  const next = new Map(vacEditDates);
                  next.set(evt.eventId, d);
                  setVacEditDates(next);
                };

                return (
                  <View key={ei} style={[styles.bulletRow, { alignItems: 'center', marginBottom: 6 }]}>
                    {/* Checkbox */}
                    <TouchableOpacity onPress={toggleCheck} style={styles.vacEditCheckbox}>
                      <View style={[styles.vacEditCheckboxInner, isChecked && styles.vacEditCheckboxOn]}>
                        {isChecked && <Text style={styles.vacEditCheckMark}>✓</Text>}
                      </View>
                    </TouchableOpacity>

                    {/* Vaccine name */}
                    <Text style={[styles.vacEventText, { flex: 1, fontWeight: isChecked ? '600' : '400', color: isChecked ? '#34495e' : '#7f8c8d' }]}>{evt.name}</Text>

                    {/* Date input — only when checked */}
                    {isChecked && (
                      Platform.OS === 'web' ? (
                        // @ts-ignore
                        <input
                          type="date"
                          value={editDate}
                          onChange={(e: any) => setDate(e.target.value)}
                          style={{
                            marginLeft: 8, border: '1px solid #d0d7de', borderRadius: 6,
                            padding: '4px 8px', fontSize: 12, color: '#2c3e50',
                            backgroundColor: '#fafbfc', outline: 'none',
                          }}
                        />
                      ) : (
                        <TouchableOpacity
                          style={styles.vacEditDateButton}
                          onPress={() => setVacDatePickerEventId(evt.eventId)}
                        >
                          <Text style={styles.vacEditDateButtonText}>{fmtDate(editDate)}</Text>
                        </TouchableOpacity>
                      )
                    )}
                    {Platform.OS !== 'web' && isChecked && (
                      <DatePickerModal
                        visible={vacDatePickerEventId === evt.eventId}
                        title={`Vaccine Date: ${evt.name}`}
                        initialValue={editDate || today}
                        yearsBack={18}
                        accentColor={appConfig.getNavButtonColor()}
                        onCancel={() => setVacDatePickerEventId(null)}
                        onConfirm={(nextDate) => {
                          setDate(nextDate);
                          setVacDatePickerEventId(null);
                        }}
                      />
                    )}
                  </View>
                );
              })}
            </View>
          );
        })}

        {/* Vaccine Key footer */}
        {(() => {
          const uniqueVaccineIds = Array.from(new Set(
            selectedVaccineSchedule.events.flatMap(e => [e.vaccineId].filter(Boolean))
          ));
          const keyVaccines = uniqueVaccineIds
            .map(id => vaccineMap.get(id))
            .filter((v): v is VaccineDto => !!v);
          if (keyVaccines.length === 0) return null;
          return (
            <View style={styles.vaccineKeyFooter}>
              {keyVaccines.map(vax => (
                <View key={vax.vaccineId} style={styles.vaccineKeyRow}>
                  <Text style={styles.vaccineKeyAcronym}>{vax.name}</Text>
                  <Text style={styles.vaccineKeyName}>{vax.dName}</Text>
                </View>
              ))}
            </View>
          );
        })()}
      </View>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  const selectedProfileIndex = profiles.findIndex(p => p.id === selectedProfileId);

  return (
    <View style={[styles.backgroundContainer, { backgroundColor: appConfig.getAppBackgroundColor() }]}>

      {/* Fixed Profile Header */}
      <View style={styles.fixedDomainHeader}>
        <View style={{ maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }}>
          <View style={styles.domainTitleRow}>
            <Text style={styles.domainTitle}>{genderIcon}  Manage Child Profiles</Text>
          </View>
          <View style={styles.navigationRow}>
            {/* Hamburger menu button — dropdown is rendered at root level via measureInWindow
                to escape the fixedDomainHeader stacking context */}
            <TouchableOpacity
              ref={hamburgerBtnRef}
              style={styles.profileMenuBtn}
              onPress={() => {
                if (showProfileMenu) {
                  setShowProfileMenu(false);
                } else {
                  hamburgerBtnRef.current?.measureInWindow((x: number, y: number, _w: number, height: number) => {
                    setMenuAnchor({ top: y + height + 4, left: x });
                    setShowProfileMenu(true);
                  });
                }
              }}
            >
              <Text style={styles.profileMenuBtnText}>{'☰'}</Text>
            </TouchableOpacity>

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ flex: 1 }}
              contentContainerStyle={[styles.profileChipRow, { paddingLeft: 4 }]}
            >
              {visibleProfiles.map(p => {
                const isActive = p.id === selectedProfileId;
                const isDisabled = !!p.isHidden;
                return (
                  <TouchableOpacity
                    key={p.id}
                    style={[styles.profileChip, isActive && styles.profileChipActive, isDisabled && styles.profileChipDisabled]}
                    onPress={() => { setSelectedProfileId(p.id); setIsEditingDetail(false); }}
                  >
                    <Text style={[styles.profileChipText, isActive && styles.profileChipTextActive, isDisabled && styles.profileChipTextDisabled]}>
                      {p.name.split(' ')[0]}{p.id === activeProfileId ? ' ★' : ''}{isDisabled ? ' ⊘' : ''}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
            <View style={styles.rightNavigationControls}>
              {(() => {
                const tabIndex = visibleTabs.findIndex(t => t.key === activeTab);
                return (
                  <>
                    <TouchableOpacity
                      style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() },
                        tabIndex <= 0 && styles.navButtonDisabled]}
                      onPress={() => {
                        if (tabIndex > 0) {
                          const nextTab = visibleTabs[tabIndex - 1].key;
                          setActiveTab(nextTab);
                          if (nextTab === 'Vaccinations') {
                            void refreshWellCheckPreferences();
                          }
                          setIsEditingDetail(false);
                        }
                      }}
                      disabled={tabIndex <= 0}
                    >
                      <Text style={[styles.navButtonText, { fontSize: 20 }]}>{'◀'}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() },
                        tabIndex >= visibleTabs.length - 1 && styles.navButtonDisabled]}
                      onPress={() => {
                        if (tabIndex < visibleTabs.length - 1) {
                          const nextTab = visibleTabs[tabIndex + 1].key;
                          setActiveTab(nextTab);
                          if (nextTab === 'Vaccinations') {
                            void refreshWellCheckPreferences();
                          }
                          setIsEditingDetail(false);
                        }
                      }}
                      disabled={tabIndex >= visibleTabs.length - 1}
                    >
                      <Text style={[styles.navButtonText, { fontSize: 20 }]}>{'▶'}</Text>
                    </TouchableOpacity>
                  </>
                );
              })()}
            </View>
          </View>
        </View>
      </View>

      {/* Tab Menu */}
      <View style={styles.tabSectionOuter}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[styles.tabContainer, { maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }]}
          contentContainerStyle={styles.tabContent}
        >
          {visibleTabs.map(t => {
            const icon = t.key === 'Detail' ? genderIcon : t.icon;
            return (
              <TouchableOpacity
                key={t.key}
                style={[styles.tab, activeTab === t.key && { backgroundColor: appConfig.getNavButtonColor() }]}
                onPress={() => {
                  setActiveTab(t.key);
                  if (t.key === 'Vaccinations') {
                    void refreshWellCheckPreferences();
                  }
                  setIsEditingDetail(false);
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                  <Text style={styles.tabIcon}>{icon}</Text>
                  <Text style={[styles.tabText, activeTab === t.key && styles.selectedTabText]}>{t.label}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {/* Main Content Card */}
      <View style={styles.cardWrapper}>
        <View style={[styles.container, { maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%' }]}>
          <ScrollView style={styles.contentScrollView} contentContainerStyle={styles.contentInner}>
            {activeTab === 'Detail'       && renderDetail()}
            {activeTab === 'Growth'       && trackHeightWeight && renderGrowth()}
            {activeTab === 'DevAge'       && renderDevAge()}
            {activeTab === 'Vaccinations' && vaccineInfoEnabled && renderVaccinations()}
          </ScrollView>
        </View>
      </View>

      {/* Profile hamburger dropdown — rendered at root level, outside fixedDomainHeader,
          so it floats above the tab navigation row regardless of stacking context */}
      {showProfileMenu && menuAnchor && (
        <TouchableOpacity
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }}
          activeOpacity={1}
          onPress={() => setShowProfileMenu(false)}
        >
          <View style={[styles.profileMenuDropdown, { top: menuAnchor.top, left: menuAnchor.left }]}>
            <TouchableOpacity
              style={[styles.profileMenuItem, !canAddProfile && styles.btnDisabled]}
              onPress={() => {
                if (!canAddProfile) {
                  setShowProfileMenu(false);
                  Alert.alert(
                    'Profile Limit Reached',
                    isPremiumAccount
                      ? `Premium accounts can have up to ${maxProfiles} profiles.`
                      : `Standard accounts can have up to ${maxProfiles} profiles. Upgrade to Premium for up to 10 profiles.`
                  );
                  return;
                }
                setShowProfileMenu(false);
                setShowAddProfile(true);
              }}
            >
              <Text style={[styles.profileMenuItemText, !canAddProfile && styles.btnDisabledText]}>
                {canAddProfile ? '+ Add New Profile' : `Profile Limit Reached (${maxProfiles})`}
              </Text>
            </TouchableOpacity>
            {hasHiddenProfiles && (
              <TouchableOpacity
                style={styles.profileMenuItem}
                onPress={() => { setShowDisabled(v => !v); setShowProfileMenu(false); }}
              >
                <Text style={styles.profileMenuItemText}>
                  {showDisabled ? '✔ Show Only Enabled' : '□ Show Disabled'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </TouchableOpacity>
      )}

      {/* Add Profile overlay — absolute fill so it covers the full screen */}
      {showAddProfile && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }}>
          <AddProfileView
            onProfileCreated={handleProfileCreated}
            onCancel={() => setShowAddProfile(false)}
          />
        </View>
      )}

      {/* Confirm-delete modal — always mounted */}
      {(() => {
        const deleteTarget = profiles.find(p => p.id === deleteTargetId);
        return (
          <Modal
            visible={!!deleteTargetId}
            transparent
            animationType="fade"
            onRequestClose={() => setDeleteTargetId(null)}
          >
            <View style={styles.modalBackdrop}>
              <View style={styles.modalCard}>
                <Text style={styles.modalTitle}>Delete Profile</Text>
                <Text style={styles.modalMessage}>
                  {'Permanently delete '}
                  <Text style={{ fontWeight: '700' }}>{deleteTarget?.name ?? ''}</Text>
                  {'?\n\nAll data for this profile — milestones, growth records, and health history — will be removed and cannot be recovered.'}
                </Text>
                <View style={styles.modalButtons}>
                  <TouchableOpacity style={styles.btnSecondary}
                    onPress={() => setDeleteTargetId(null)}>
                    <Text style={styles.btnSecondaryText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.btnDestructive} onPress={confirmDelete}>
                    <Text style={styles.btnDestructiveText}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>
        );
      })()}

      {/* Confirm-disable modal — always mounted, toggled via visible prop to avoid
          mount-delay issues on web caused by the IIFE conditional pattern */}
      {(() => {
        const disableTarget = profiles.find(p => p.id === disableTargetId);
        return (
          <Modal
            visible={!!disableTargetId}
            transparent
            animationType="fade"
            onRequestClose={() => setDisableTargetId(null)}
          >
            <View style={styles.modalBackdrop}>
              <View style={styles.modalCard}>
                <Text style={styles.modalTitle}>Disable Profile</Text>
                <Text style={styles.modalMessage}>
                  {'Disable '}
                  <Text style={{ fontWeight: '700' }}>{disableTarget?.name ?? ''}</Text>
                  {'\n\nThis profile will be hidden from the home screen and profile selector. You can re-enable it at any time from Manage Profiles.'}
                </Text>
                <View style={styles.modalButtons}>
                  <TouchableOpacity style={styles.btnSecondary}
                    onPress={() => setDisableTargetId(null)}>
                    <Text style={styles.btnSecondaryText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.btnDanger} onPress={confirmDisable}>
                    <Text style={styles.btnDangerText}>Disable</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>
        );
      })()}
      {Platform.OS !== 'web' && (
        <DatePickerModal
          visible={showDobPicker}
          title="Date of Birth"
          initialValue={normalizeDateOnly(editDob) || normalizeDateOnly(selectedProfile?.dob) || new Date().toISOString().slice(0, 10)}
          yearsBack={30}
          accentColor={appConfig.getNavButtonColor()}
          onCancel={() => setShowDobPicker(false)}
          onConfirm={(nextDate) => {
            setEditDob(normalizeDateOnly(nextDate));
            setShowDobPicker(false);
          }}
        />
      )}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backgroundContainer: { flex: 1 },

  fixedDomainHeader: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    paddingTop: 16 + appConfig.getMobileHeaderTopPadding(),
    paddingBottom: 16,
    paddingHorizontal: 24,
  },
  domainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  domainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  navigationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftNavigationControls: {
    flexDirection: 'row',
    gap: 8,
    minWidth: 36,
  },
  rightNavigationControls: {
    flexDirection: 'row',
    gap: 8,
    marginLeft: 8,
    flexShrink: 0,
  },
  profileChipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 2,
  },
  profileChip: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: appConfig.getNavButtonColorInactive(),
  },
  profileChipActive: {
    backgroundColor: appConfig.getNavButtonColor(),
  },
  profileChipDisabled: {
    opacity: 0.55,
    borderWidth: 1,
    borderColor: '#ccc',
  },
  profileChipText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  profileChipTextActive: {
    color: '#ffffff',
  },
  profileChipTextDisabled: {
    color: '#999',
  },
  profileMenuBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: appConfig.getNavButtonColorInactive(),
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 4,
    flexShrink: 0,
  },
  profileMenuBtnText: {
    fontSize: 16,
    color: '#5f6368',
    lineHeight: 18,
  },
  profileMenuOverlay: {
    position: 'absolute',
    top: 38,
    left: 0,
    zIndex: 999,
    // covers the screen so tapping outside closes menu
    width: (9999 as any),
    height: (9999 as any),
  },
  profileMenuDropdown: {
    position: 'absolute',
    top: 0,
    left: 0,
    minWidth: 190,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e4e8',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 6,
    overflow: 'hidden',
  },
  profileMenuItem: {
    paddingHorizontal: 16,
    paddingVertical: 11,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f2f5',
  },
  profileMenuItemText: {
    fontSize: 13,
    color: '#2c3e50',
    fontWeight: '500',
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: appConfig.getNavButtonColor(),
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  navButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },

  tabSectionOuter: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
  },
  tabContainer: {
    maxHeight: 60,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'center',
  },
  tab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: appConfig.getNavButtonColorInactive(),
    minWidth: 100,
    alignItems: 'center',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  tabIcon: {
    fontSize: 15,
  },
  selectedTabText: {
    color: '#ffffff',
  },

  cardWrapper: {
    flex: 1,
    paddingTop: 12,
    paddingHorizontal: appConfig.getCardMargin(),
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  contentScrollView: {
    flex: 1,
  },
  contentInner: {
    padding: 16,
    paddingBottom: 32,
  },

  card: {
    backgroundColor: '#ffffff', borderRadius: 12, padding: 16,
    marginHorizontal: 16, marginBottom: 16,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08, shadowRadius: 4, elevation: 2,
  },
  cardTitle: { fontSize: 16, fontWeight: '700', color: '#2c3e50', marginBottom: 4 },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4, justifyContent: 'space-between' },
  cardSubtitle: { fontSize: 12, color: '#7f8c8d', marginBottom: 12 },
  cardNote: { fontSize: 11, color: '#95a5a6', marginTop: 10, fontStyle: 'italic' },

  emptyText: { fontSize: 14, color: '#7f8c8d', textAlign: 'center', paddingVertical: 20 },

  infoRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start',
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f2f5',
  },
  infoLabel: { fontSize: 13, color: '#7f8c8d', flex: 3 },
  infoValue: { fontSize: 13, color: '#2c3e50', fontWeight: '500', flex: 7, textAlign: 'left' },
  syncIconButton: {
    width: 30, height: 30, borderRadius: 6,
    backgroundColor: '#eaf4fb', borderWidth: 1, borderColor: '#3498db',
    justifyContent: 'center', alignItems: 'center',
  },
  syncIconButtonDisabled: { opacity: 0.5 },
  syncIconText: { fontSize: 16 },

  editBtn: {
    paddingHorizontal: 12, paddingVertical: 5, borderRadius: 6,
    backgroundColor: '#eaf4fb', borderWidth: 1, borderColor: '#3498db',
  },
  editBtnText: { fontSize: 12, color: '#3498db', fontWeight: '600' },

  fieldLabel: { fontSize: 12, color: '#7f8c8d', marginBottom: 4, marginTop: 8 },
  textInput: {
    borderWidth: 1, borderColor: '#d0d7de', borderRadius: 6, padding: 8,
    fontSize: 14, color: '#2c3e50', backgroundColor: '#fafbfc', marginBottom: 4,
  },
  editCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 4,
  },
  editCheckboxBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d0d7de',
    backgroundColor: '#fafbfc',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editCheckboxBoxChecked: {
    backgroundColor: '#27ae60',
    borderColor: '#27ae60',
  },
  editCheckboxCheck: {
    color: '#ffffff',
    fontWeight: '700',
    fontSize: 12,
  },
  premChipRow: {
    flexDirection: 'row',
    gap: 8,
    paddingVertical: 4,
    paddingRight: 8,
  },
  premScrollRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  premScrollRowMobile: {
    gap: 0,
  },
  premScrollView: {
    flex: 1,
  },
  premScrollButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#d0d7de',
    backgroundColor: '#fafbfc',
    alignItems: 'center',
    justifyContent: 'center',
  },
  premScrollButtonText: {
    fontSize: 10,
    color: '#2c3e50',
    fontWeight: '700',
  },
  premChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#d0d7de',
    backgroundColor: '#fafbfc',
  },
  premChipActive: {
    borderColor: '#3498db',
    backgroundColor: '#3498db',
  },
  premChipText: {
    fontSize: 12,
    color: '#2c3e50',
    fontWeight: '600',
  },
  premChipTextActive: {
    color: '#ffffff',
  },
  genderRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  genderChip: {
    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16,
    borderWidth: 1, borderColor: '#d0d7de', backgroundColor: '#f8f9fa',
  },
  genderChipActive: { borderColor: '#3498db', backgroundColor: '#eaf4fb' },
  genderChipText: { fontSize: 12, color: '#555e6b' },
  genderChipTextActive: { color: '#3498db', fontWeight: '600' },

  tableHeader: {
    flexDirection: 'row', paddingVertical: 6, paddingHorizontal: 4,
    borderBottomWidth: 2, borderBottomColor: '#e0e4ea', marginBottom: 2,
  },
  tableHeaderCell: { fontSize: 11, fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase' },
  tableRow: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 4 },
  tableRowEven: { backgroundColor: '#f8f9fb' },
  tableRowOdd: { backgroundColor: '#ffffff' },
  tableCell: { fontSize: 13, color: '#2c3e50' },

  manageProfileHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  manageProfileName: { fontSize: 15, fontWeight: '700', color: '#2c3e50' },
  manageProfileMeta: { fontSize: 12, color: '#7f8c8d', marginTop: 2 },
  manageStatsRow: { flexDirection: 'row', gap: 16, marginBottom: 14, flexWrap: 'wrap' },
  manageStat: { fontSize: 12, color: '#7f8c8d' },

  btnPrimary: {
    flex: 1, paddingVertical: 10, paddingHorizontal: 16, borderRadius: 8,
    backgroundColor: '#3498db', alignItems: 'center',
  },
  btnPrimaryText: { fontSize: 14, color: '#ffffff', fontWeight: '600' },
  btnSecondary: {
    paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8,
    borderWidth: 1, borderColor: '#d0d7de', backgroundColor: '#f8f9fa', alignItems: 'center',
  },
  btnSecondaryText: { fontSize: 13, color: '#555e6b', fontWeight: '500' },
  btnDanger: {
    paddingVertical: 9, paddingHorizontal: 14, borderRadius: 8,
    borderWidth: 1, borderColor: '#e74c3c', backgroundColor: '#fdf2f2', alignItems: 'center',
  },
  btnDangerText: { fontSize: 13, color: '#e74c3c', fontWeight: '600' },
  btnDestructive: {
    paddingVertical: 9, paddingHorizontal: 14, borderRadius: 8,
    borderWidth: 1, borderColor: '#c0392b', backgroundColor: '#c0392b', alignItems: 'center',
  },
  btnDestructiveText: { fontSize: 13, color: '#ffffff', fontWeight: '600' },
  btnDisabled: { borderColor: '#d0d7de', backgroundColor: '#f8f9fa' },
  btnDisabledText: { color: '#bdc3c7' },

  // ── Vaccine period-card styles (mirrors GuidanceView) ──────────────────────
  vacPeriodCard: {
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
    paddingBottom: 6,
  },
  vacPeriodTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: 6,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
    paddingLeft: 2,
  },
  bulletDot: {
    fontSize: 14,
    color: '#3498db',
    marginRight: 6,
    marginTop: 1,
    lineHeight: 20,
  },
  vacEventText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#34495e',
    flex: 1,
    lineHeight: 20,
  },
  vacEventDetail: {
    fontSize: 12,
    fontWeight: '400',
    color: '#7f8c8d',
  },
  vaccineNote: {
    backgroundColor: '#eaf4fb',
    padding: 14,
    borderRadius: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  vaccineNoteText: {
    fontSize: 13,
    color: '#34495e',
    lineHeight: 20,
  },
  vacEditLink: {
    fontSize: 12,
    color: '#3498db',
    fontWeight: '500',
  },
  vacEditCheckbox: {
    marginRight: 8,
    flexShrink: 0,
  },
  vacEditCheckboxInner: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#bdc3c7',
    backgroundColor: '#f8f9fa',
    alignItems: 'center',
    justifyContent: 'center',
  },
  vacEditCheckboxOn: {
    borderColor: '#27ae60',
    backgroundColor: '#27ae60',
  },
  vacEditCheckMark: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '700',
    lineHeight: 16,
  },
  vacEditDateInput: {
    marginLeft: 8,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    fontSize: 12,
    color: '#2c3e50',
    backgroundColor: '#fafbfc',
    minWidth: 110,
  },
  vacEditDateButton: {
    marginLeft: 8,
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: '#fafbfc',
    minWidth: 110,
    justifyContent: 'center',
  },
  vacEditDateButtonText: {
    fontSize: 12,
    color: '#2c3e50',
  },
  mobileDateButton: {
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#fafbfc',
  },
  mobileDateButtonText: {
    fontSize: 14,
    color: '#2c3e50',
  },
  mobileDateButtonPlaceholder: {
    color: '#8f9aa6',
  },
  vaccineKeyFooter: {
    marginTop: 4,
    marginLeft: 40,
    marginRight: 20,
    paddingBottom: 24,
  },
  vaccineKeyRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    paddingVertical: 2,
  },
  vaccineKeyAcronym: {
    fontSize: 11,
    fontWeight: '600',
    color: '#8a9ab0',
    width: 72,
    flexShrink: 0,
  },
  vaccineKeyName: {
    fontSize: 11,
    color: '#aab3be',
    flex: 1,
    flexWrap: 'wrap',
  },

  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: 24,
  },
  modalCard: {
    backgroundColor: '#ffffff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 12, elevation: 8,
  },
  modalTitle: { fontSize: 17, fontWeight: '700', color: '#2c3e50', marginBottom: 12 },
  modalMessage: { fontSize: 14, color: '#555e6b', lineHeight: 20, marginBottom: 24 },
  modalButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },
});
